import {Show} from 'platform/foundation';

import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useAssistantEntityContext} from '@dms/ai-assistant';
import {useGetRentalOrderQuery} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {
  getRentalOrderAssistantEntities,
  useAssistantRentalOrderContext,
} from '@dms/features/ai-assistant-rental';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import i18n from '@dms/i18n';
import {rentalOrderRoutes, testIds} from '@dms/routes';
import {
  CommunicationSection,
  EMPTY_PLACEHOLDER,
  getCommunicationSectionTitle,
  NavigationItem,
  Page,
} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

import {ActivityLogs} from './(Sections)/ActivityLogs/ActivityLogs';
import {CustomFields} from './(Sections)/CustomFields/CustomFields';
import {Documents} from './(Sections)/Documents/Documents';
import {RentalOrderHeader} from './(Sections)/RentalOrderHeader/RentalOrderHeader';
import {RentalOrderPhotos} from './(Sections)/RentalOrderPhotos/RentalOrderPhotos';
import {RentVehicle} from './(Sections)/RentVehicle/RentVehicle';
import {ReturnVehicle} from './(Sections)/ReturnVehicle/ReturnVehicle';
import {ServiceOrder} from './(Sections)/ServiceOrder/ServiceOrder';
import {VehicleAndCustomer} from './(Sections)/VehicleAndCustomer/VehicleAndCustomer';
import {RentalOrderActivityPanel} from './components/RentalOrderActivityPanel/RentalOrderActivityPanel';

type NavigationRoute =
  | typeof rentalOrderRoutes.vehicleAndCustomer
  | typeof rentalOrderRoutes.returnVehicle
  | typeof rentalOrderRoutes.rentVehicle
  | typeof rentalOrderRoutes.documents
  | typeof rentalOrderRoutes.activityLogs
  | typeof rentalOrderRoutes.communication
  | typeof rentalOrderRoutes.photos
  | typeof rentalOrderRoutes.serviceOrder
  | typeof rentalOrderRoutes.customFields
  | typeof rentalOrderRoutes.customNotices;

export function RentalOrderDetail() {
  const navigate = useNavigate();
  const {id: defaultRentalOrderId} = useParams();
  const [rentalOrderId, setRentalOrderId] = useState(defaultRentalOrderId);

  useEffect(
    function syncRentalOrderId() {
      setRentalOrderId(defaultRentalOrderId);
    },
    [defaultRentalOrderId]
  );

  const {data: rentalOrder, isLoading: isLoadingRentalOrder} = useGetRentalOrderQuery(
    {rentalOrderId: rentalOrderId ?? ''},
    {skip: isNil(rentalOrderId), refetchOnMountOrArgChange: true}
  );

  const {
    ownerRef: assistantContextOwnerRef,
    synchronizeRef: synchronizeAssistantContextRef,
    vehicleLabel,
    customerLabel,
  } = useAssistantRentalOrderContext(rentalOrderId);
  const {ownerRef: assistantEntitiesOwnerRef, synchronizeRef: synchronizeAssistantEntitiesRef} =
    useAssistantEntityContext(
      getRentalOrderAssistantEntities(rentalOrderId, rentalOrder, {
        vehicle: vehicleLabel,
        customer: customerLabel,
      })
    );

  const handleSetRentalOrderId = (rentalOrderId: string | undefined) => {
    if (isNil(rentalOrderId)) {
      throw new Error('Rental order ID is required');
    }

    setRentalOrderId(rentalOrderId);
    navigate(composePath(rentalOrderRoutes.vehicleAndCustomer, {params: {id: rentalOrderId}}), {
      replace: true,
    });
  };

  const getNavigationHref = (route: NavigationRoute) => {
    if (isNil(rentalOrderId)) {
      return rentalOrderRoutes.create;
    }

    return composePath(route, {
      params: {id: rentalOrderId},
    });
  };

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.rentalOrder,
    recordId: rentalOrderId,
  });

  const rentInspectionId = rentalOrder?.rentVehicleInspections?.[0];
  const returnInspectionId = rentalOrder?.returnVehicleInspections?.[0];

  const hasNoInspections = isNilOrEmpty(rentInspectionId) && isNilOrEmpty(returnInspectionId);

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.customerMessage.labels.contextResource.RENTAL_ORDER')}: ${rentalOrder?.number ?? EMPTY_PLACEHOLDER}`,
  };

  const navigationItems = buildArray<NavigationItem>([
    {
      id: 'rental-order-vehicleAndCustomer',
      label: i18n.t('page.rentalOrder.labels.vehicleAndCustomer'),
      hasCounter: true,
      href: getNavigationHref(rentalOrderRoutes.vehicleAndCustomer),
      content: (
        <VehicleAndCustomer
          rentalOrderId={rentalOrderId}
          setRentalOrderId={handleSetRentalOrderId}
        />
      ),
      'data-testid': testIds.rentalOrder.vehicleAndCustomer('navigation-vehicleAndCustomer'),
    },
    {
      id: 'rental-order-rentVehicle',
      hasCounter: true,
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.rentVehicle'),
      href: getNavigationHref(rentalOrderRoutes.rentVehicle),
      content: [
        {
          route: rentalOrderRoutes.rentVehicle,
          element: <RentVehicle maximization={maximization} />,
        },
        {
          route: rentalOrderRoutes.rentVehicleInspections,
          element: <RentVehicle maximization={maximization} />,
        },
      ],
      'data-testid': testIds.rentalOrder.rentVehicle('navigation-rentVehicle'),
    },
    {
      id: 'rental-order-returnVehicle',
      hasCounter: true,
      hasSeparator: true,
      isDisabled:
        isNil(rentalOrder) || rentalOrder?.state === 'draft' || rentalOrder?.state === 'reserved',
      label: i18n.t('page.rentalOrder.labels.returnVehicle'),
      href: getNavigationHref(rentalOrderRoutes.returnVehicle),
      content: [
        {
          route: rentalOrderRoutes.returnVehicle,
          element: <ReturnVehicle maximization={maximization} />,
        },
        {
          route: rentalOrderRoutes.returnVehicleInspections,
          element: <ReturnVehicle maximization={maximization} />,
        },
      ],
      'data-testid': testIds.rentalOrder.returnVehicle('navigation-returnVehicle'),
    },
    {
      id: 'rental-order-serviceOrder',
      label: i18n.t('page.rentalOrder.labels.serviceOrder'),
      href: getNavigationHref(rentalOrderRoutes.serviceOrder),
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      content: <ServiceOrder />,
      'data-testid': testIds.rentalOrder.serviceOrder('navigation-serviceOrder'),
    },
    {
      id: 'rental-order-photos',
      label: i18n.t('page.rentalOrder.labels.photos'),
      href: getNavigationHref(rentalOrderRoutes.photos),
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft' || hasNoInspections,
      content: <RentalOrderPhotos />,
      'data-testid': testIds.rentalOrder.photos('navigation-photos'),
    },
    {
      id: 'rental-order-documents',
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.documents'),
      href: getNavigationHref(rentalOrderRoutes.documents),
      content: <Documents maximization={maximization} />,
      hasSeparator: true,
      'data-testid': testIds.rentalOrder.documents('navigation-documents'),
    },
  ])
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: 'rental-order-customNotices',
      isDisabled: isNil(rentalOrder),
      label: i18n.t('general.customNotices.labels.title'),
      href: getNavigationHref(rentalOrderRoutes.customNotices),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.rentalOrder}
          recordId={rentalOrderId ?? ''}
          data-testid={testIds.rentalOrder.detail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      'data-testid': testIds.rentalOrder.detail('navigation-customNotices'),
    })
    .add({
      id: 'rental-order-customFields',
      label: i18n.t('page.generalSettings.labels.customFields'),
      href: getNavigationHref(rentalOrderRoutes.customFields),
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      content: <CustomFields rentalOrderId={rentalOrderId} />,
      'data-testid': testIds.rentalOrder.customFields('navigation-customFields'),
    })
    .add({
      id: 'rental-order-activityLogs',
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: i18n.t('page.rentalOrder.labels.activityLogs'),
      href: getNavigationHref(rentalOrderRoutes.activityLogs),
      content: [
        {
          route: rentalOrderRoutes.activityLogs,
          element: <ActivityLogs maximization={maximization} />,
        },
      ],
      'data-testid': testIds.rentalOrder.activityLogs('navigation-activityLogs'),
    })
    .add({
      id: 'rental-order-communication',
      isDisabled: isNil(rentalOrder) || rentalOrder?.state === 'draft',
      label: getCommunicationSectionTitle(),
      href: getNavigationHref(rentalOrderRoutes.communication),
      content: (
        <CommunicationSection
          maximization={maximization}
          customerId={rentalOrder?.customerId ?? ''}
          recordId={rentalOrderId ?? ''}
          resourceId={EntityResourceIds.rentalOrder}
          data-testid="rental-order-communication"
        />
      ),
      'data-testid': testIds.rentalOrder.activityLogs('navigation-communication'),
    });

  return (
    <>
      <div ref={assistantContextOwnerRef} hidden>
        <span ref={synchronizeAssistantContextRef} aria-hidden />
      </div>
      <div ref={assistantEntitiesOwnerRef} hidden>
        <span ref={synchronizeAssistantEntitiesRef} aria-hidden />
      </div>
      <Page
        key={rentalOrderId}
        navigation={navigationItems}
        isLoading={isLoadingRentalOrder}
        isFullHeight
        activityPanel={
          <Show when={rentalOrderId}>
            <RentalOrderActivityPanel rentalOrderId={rentalOrderId} />
          </Show>
        }
        header={
          <RentalOrderHeader
            rentalOrderId={rentalOrderId}
            data-testid={testIds.rentalOrder.detail('header')}
          />
        }
        data-testid={testIds.rentalOrder.detail('page')}
      />
    </>
  );
}
